import _ from 'lodash';

// Spec validation for the builder store.
// Logic is mirrored in ItemService PHP; keep this and PHP in sync.

export function validateSpec(val, rules, ctx) {
  const quote = ctx.quote;
  const currentItem = ctx.currentItem;
  const showBenchtopMaterialOptions = ctx.showBenchtopMaterialOptions;

  // init
  const handle = rules.handle;
  const name = rules.name ?? rules.handle ?? '';
  const sections = rules.sections ?? false;
  let required = rules.required ?? false;
  const type = rules.type ?? 'string';
  const scope = rules.scope ?? 'specs';
  const step = rules.step ?? 1;
  const conditions = rules.conditions ?? false;
  const checkValue = rules.value ?? null;
  const requireEmail = rules.requireEmail ?? false;
  const msg = rules.message ?? null;
  let isValid = true;
  let errors = [];
  let keepValidating = true;

  // if scope isn't specs, get proper value
  let value = val;
  if (scope == 'quote') {
    value = quote[handle];
  }

  // conditions
  if (conditions) {
    let conditionsMet = 0;
    for (let i = 0; i < conditions.length; i++) {
      const conditionScope = conditions[i].scope ?? 'specs';
      const conditionHandle = conditions[i].handle;
      const conditionValue = conditions[i].value;
      const conditionOperator = conditions[i].operator ?? '=';
      if (conditionOperator == '>=') {
        if (currentItem[conditionScope][conditionHandle] >= conditionValue) conditionsMet ++;
      } else {
        if (currentItem[conditionScope][conditionHandle] == conditionValue) conditionsMet ++;
      }
    }
    if (conditionsMet == conditions.length) {
      required = true;
    } else {
      keepValidating = false;
    }
  }

  // special conditions
  if (handle == 'benchId') {
    if (!showBenchtopMaterialOptions) {
      required = false;
    }
  }

  // required
  if (keepValidating && required) {
    if (_.isNull(value) || value.length == 0) {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' is required'
      });
      keepValidating = false;
    }
  }

  // value
  if (keepValidating && checkValue) {
    if (value !== checkValue) {
      isValid = false;
      errors.push({
        handle: handle,
        message: msg ?? name + ' must be equal to ' + checkValue
      });
      keepValidating = false;
    }
  }

  // integers + mods
  if (keepValidating && type == 'int') {

    // check if integer
    if (!_.isNull(value) && !_.isNumber(value)) {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' must be an integer'
      });
      keepValidating = false;
    }

  }

  // float number
  if (keepValidating && type == 'float') {

    // check if float
    if (!_.isNull(value) && !_.isNumber(value)) {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' must be an number'
      });
      keepValidating = false;
    }

  }

  // integers
  if (keepValidating && type == 'int' && !quote.isCustom) {

    const min = rules.min ?? null;
    const max = rules.max ?? null;

    // min
    if (keepValidating && min && value < min) {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' must be greater than ' + min
      });
      keepValidating = false;
    }

    // max
    if (keepValidating && max && value > max) {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' must be less than ' + max
      });
      keepValidating = false;
    }

  }

  // email
  if (keepValidating && type == 'email') {

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      isValid = false;
      errors.push({
        handle: 'email',
        message: 'Email is invalid'
      });
      keepValidating = false;
    }
  }

  // mods
  if (keepValidating && type == 'mod') {

    // we only validate mods on the server-side

  }

  // boolean
  if (keepValidating && type == 'boolean') {

    // check if true or false
    if (!_.isNull(value) && typeof value !== 'boolean') {
      isValid = false;
      errors.push({
        handle: handle,
        message: name + ' must be boolean'
      });
      keepValidating = false;
    }

  }

  return {
    step: step,
    valid: isValid,
    errors: errors
  };
}

export function validateItem({
  item,
  structure,
  steps,
  step,
  section,
  sectionMax,
  ctx
}) {
  let validate = [
    {
      valid: true,
      errors: []
    }
  ];
  let stepWithSections = null;

  // setup validation results array
  if (steps.length) {
    validate = [];
    steps.forEach((stepDef, index) => {
      validate.push({
        valid: true,
        errors: []
      });
      if (!_.isUndefined(stepDef.hasSections) && stepDef.hasSections) {
        stepWithSections = stepDef.number;
        validate[index].sections = [];
        item.productLayoutSections.forEach((layoutSection, sectionIndex) => {
          validate[index].sections.push({
            valid: true,
            errors: []
          });
        });
      }
    });
  }

  // core specs
  const coreSpecs = ['productId', 'productLayoutId'];
  const coreSpecsNames = ['Product', 'Product Layout'];
  let isCoreValid = true;
  coreSpecs.forEach((spec, index) => {
    const rules = {
      handle: spec,
      name: coreSpecsNames[index],
      type: 'integer',
      required: true,
      step: 1
    };
    const value = item[spec] ?? null;
    if (rules) {
      const results = validateSpec(value, rules, ctx);
      if (!results.valid) {
        validate[results.step - 1].valid = results.valid;
        validate[results.step - 1].errors = validate[results.step - 1].errors.concat(results.errors);
        isCoreValid = false;
      }
    }
  });

  // loop through all specs and sections with validation rules
  // only if we're on a step that's past step 1
  if (structure && steps.length && step > 1) {
    for (const [handle, value] of Object.entries(item.specs)) {

      if (handle == 'sections') {

        if (!_.isUndefined(structure[handle]) && !_.isUndefined(structure[handle].sections) && _.isObject(structure[handle].sections)) {
          item.specs.sections.forEach((itemSection, sectionIndex) => {
            for (const [sHandle, sValue] of Object.entries(itemSection)) {
              let getRules = structure[handle].sections[sHandle] ?? null;
              let rules = JSON.parse(JSON.stringify(getRules));
              const layoutSection = item.productLayoutSections[sectionIndex];
              if (sHandle == 'width' && layoutSection.minWidth) rules.min = layoutSection.minWidth;
              if (rules) {
                rules.step = structure[handle].step;
                const results = validateSpec(sValue, rules, ctx);
                if (!results.valid) {
                  if (!_.isUndefined(validate[results.step - 1].sections) && !_.isUndefined(validate[results.step - 1].sections[sectionIndex])) {
                    validate[results.step - 1].sections[sectionIndex].valid = results.valid;
                    validate[results.step - 1].sections[sectionIndex].errors = validate[results.step - 1].sections[sectionIndex].errors.concat(results.errors);

                    // add the current section errors to the step errors
                    if (section == (sectionIndex + 1)) {
                      validate[results.step - 1].valid = results.valid;
                      validate[results.step - 1].errors = validate[results.step - 1].errors.concat(results.errors);
                    }
                  }
                }
              }
            }
          });
        }


      } else {

        const rules = structure[handle] ?? null;
        if (rules) {
          const results = validateSpec(value, rules, ctx);
          if (!results.valid) {
            validate[results.step - 1].valid = results.valid;
            validate[results.step - 1].errors = validate[results.step - 1].errors.concat(results.errors);
          }
        }

      }

    }
  }

  let validStep = 1;
  for (let i = 0; i < steps.length; i ++) {
    validStep = steps[i].number;
    if (!validate[i].valid) {
      break;
    }
  };

  let validSection = 1;
  if (stepWithSections) {
    for (let i = 0; i < sectionMax; i ++) {
      validSection = i+1;
      if (_.isUndefined(validate[stepWithSections - 1].sections[i]) || !validate[stepWithSections - 1].sections[i].valid) {
        break;
      }
    }
  }

  if (isCoreValid && validStep < 2) validStep = 2;

  return {
    step: validStep,
    section: validSection,
    results: validate
  };
}
